/**
 * This file (for now) contains all actions that can be dispatched (store.dispatch({})).
 * These are all the actions that can be dispatched.
 *
 * These actions are called from async actions (in this file) as well as from UI components that
 * are dispatching actions.
 */

import { get, isEmpty } from 'lodash'
import $ from 'jquery'
import log from 'loglevel'
import { fetch, post, deleteAjax, patch } from '../../calculator/lib/xhr'
import { INSTRUMENTATION_TYPES, ActionTypes as types } from '../constants'
import { routeTo } from '../utils/routeHelper'
import { removeMailto } from '../utils/remove-mailto'
import { getUrlFragment } from '../utils/url-utils'
import { requestMatchesResponse } from '../utils/requestMatchesResponse'
import { shouldFetchNewAmount, isZero } from '../../calculator/actions/utils'
import { isPhone, isEmail } from '../../../../shared/validators'
import {
  getUSNumberFormatter,
  getLocalizedNumberFormatter,
} from '../../lib/amountUtils'
import serverProps from '../utils/serverProps'
import { prepareXBInitialState } from '../utils/xbFeeCalcUtils'
import { mapDefaultCurrencyKeys } from '../../experiments/defaultCurrency'
import { changeUnilateralCountryCode } from './unilateral'
import { addCPLData, setPersistentData } from '../../lib/analytics/fpti'
import { decorateContact } from 'lib/contact-utils'
import {
  toggleContactBlockedOverpanel,
  toggleContactUnblockedOverpanel,
} from 'transfer/actions/overpanels'
import { trackContactBlockedErrorImpression } from 'transfer/utils/blockUnblockAnalytics'

import { fromChoiceToReview } from './analytics-helpers'
import React from 'react'
import {
  Preview,
  MultiRequestSplitPreview,
  RequestSuccess,
} from '../lazy-components'
import { ENV_TYPE } from '../../../../lib/constants'
import * as UnifiedActions from './unified-actions'
import { tracking } from '../../lib/analytics/mParticle'
import Events from '../../lib/analytics/eventTags'

// create some number formatting functions with the proper decimal separator
const { currencyFormat: { decimal = '.' } = {} } = serverProps
const formatLocalizedNumber = getLocalizedNumberFormatter(decimal)
const formatUSNumber = getUSNumberFormatter(decimal)

/**
 * Helper to reduce boilerplate for `meta` for analytics
 * @param  {String} type    tracking type [error, action (doesn't change pageName), normal (changes pageName)]
 * @param  {String} payload the stuff we need to track (linkName, pageName, etc)
 * @return {Object}         analytics metadata
 */
export function getAnalyticsMeta(type, payload) {
  return { analytics: { type, payload } }
}

export function showError(
  err,
  {
    silenceFpti = false,
    customComp = { use: false, name: null },
    onClosed,
  } = {}
) {
  if (err && (err.code === 'blockedSend' || err.code === 'blockedRequest')) {
    trackContactBlockedErrorImpression()
  }

  log.error(err)
  return {
    type: types.ERROR.SHOW_ERROR_OVERPANEL,
    meta: silenceFpti
      ? undefined
      : getAnalyticsMeta(INSTRUMENTATION_TYPES.ERROR, { error: err }),
    data: {
      error: {
        ...err,
        // special case here because the server can sometimes send HTML
        // for the content. There is no user input in these messages
        // so it's fine, but if there ever were to be, the server would
        // have to make sure and escape that content themselves, otherwise
        // this would open us up to a XSS attack
        // eslint-disable-next-line react/no-danger
        renderedMessage: (
          <div dangerouslySetInnerHTML={{ __html: err.message }} />
        ),
        customComponent: customComp,
        onClosed,
      },
    },
  }
}

export function showErrorPage(err) {
  return {
    type: types.ERROR.SHOW_ERROR_PAGE,
    data: {
      error: err,
    },
  }
}

export function handleError(dispatch) {
  return function onError(err) {
    dispatch(showError(err))
  }
}

/**
 * Handy function to reset the window to position 0,0
 */
export function resetScrollPosition() {
  $(document.body).animate({ scrollTop: 0 }, 400)
}

export function changeSelectedFundingOption(id, analyticsData = {}) {
  return {
    type: types.TRANSACTION.CHANGE_SELECTED_FUNDING_OPTION,
    meta:
      Object.keys(analyticsData).length && fromChoiceToReview(analyticsData),
    data: { id },
  }
}

export function changeInstallmentPlan(paymentTimes) {
  paymentTimes = parseInt(paymentTimes, 10) || 0
  return {
    type: types.TRANSACTION.CHANGE_SELECTED_INSTALLMENT_PLAN,
    data: { paymentTimes },
  }
}

export function changeCountry(country) {
  return (dispatch, getState) => {
    const { server: { guestSupportedCountries = [] } = {} } = getState()

    const countryCanGuest = guestSupportedCountries.includes(country)

    dispatch({
      type: types.USER.CHANGE_COUNTRY,
      data: {
        country,
        countryCanGuest,
      },
    })
  }
}

export function changeEmail(email) {
  return {
    type: types.USER.CHANGE_EMAIL,
    data: { email },
  }
}

export function changeIsPurchase(
  isPurchase,
  { isToggle = false, shouldDefaultToSuggestedPaymentType } = {}
) {
  return dispatch => {
    const pageGroup = `main:p2p:buy::review`
    const pageName = `main:p2p:buy:review:node::`
    const nextPageGroup = `main:p2p:send::review`
    const nextPageName = `main:p2p:send::review:node::`
    const linkName = 'gotofnf'

    if (!isToggle) {
      dispatch({
        type: types.TRANSACTION.CHANGE_IS_PURCHASE,
        data: { isPurchase, shouldDefaultToSuggestedPaymentType },
      })
    } else {
      dispatch({
        type: types.TRANSACTION.CHANGE_IS_PURCHASE,
        meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
          linkName,
          pageGroup: pageGroup,
          pageName: pageName,
          nextPageGroup: nextPageGroup,
          nextPageName: nextPageName,
          trackType: 'link',
        }),
        data: { isPurchase, shouldDefaultToSuggestedPaymentType },
      })
    }
  }
}

export function hasRedirectedFromSend() {
  return (dispatch, getState) => {
    const { recipient: { recipient = '' } = {} } = getState()

    const emailOrPhone = isEmail(recipient) ? 'email' : 'phone'
    const pageGroup = `main:p2p:send::${emailOrPhone}`
    const pageName = `main:p2p:send::${emailOrPhone}`
    const nextPageGroup = `main:p2p:buy::review`
    const nextPageName = `main:p2p:buy::review:node::`
    const linkName = 'gotogns'

    dispatch({
      type: types.TRANSACTION.HAS_REDIRECT_FROM_SEND,
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
        linkName,
        pageGroup: pageGroup,
        pageName: pageName,
        nextPageGroup: nextPageGroup,
        nextPageName: nextPageName,
        trackType: 'link',
      }),
    })
  }
}

export function changeFirstName(firstName) {
  return {
    type: types.USER.CHANGE_FIRST_NAME,
    data: { firstName },
  }
}

export function changeLastName(lastName) {
  return {
    type: types.USER.CHANGE_LAST_NAME,
    data: { lastName },
  }
}

export function changePhoneNumber(phoneNumber) {
  return {
    type: types.USER.CHANGE_PHONE_NUMBER,
    data: { phoneNumber },
  }
}

export function changePrimaryCurrencyCode(primaryCurrencyCode) {
  return {
    type: types.USER.CHANGE_PRIMARY_CURRENCY_CODE,
    data: { primaryCurrencyCode },
  }
}

export function changeGovernmentId(governmentId) {
  return {
    type: types.USER.CHANGE_GOVT_ID,
    data: { governmentId },
  }
}

export function changeSelectedGovernmentIDType(selectedGovernmentIdType) {
  return {
    type: types.USER.CHANGE_SELECTED_GOVERNMENT_ID_TYPE,
    data: { selectedGovernmentIdType },
  }
}

export function changeBirthDay(birthDay) {
  return {
    type: types.USER.CHANGE_BIRTH_DAY,
    data: { birthDay },
  }
}

export function changeBirthMonth(birthMonth) {
  return {
    type: types.USER.CHANGE_BIRTH_MONTH,
    data: { birthMonth },
  }
}

export function changeBirthYear(birthYear) {
  return {
    type: types.USER.CHANGE_BIRTH_YEAR,
    data: { birthYear },
  }
}

function updateRecipientCurrencies({
  recipientCurrencyCode,
  recipientCurrencies,
  recipientCurrencyMap,
}) {
  return {
    type: types.AMOUNT.UPDATE_RECIPIENT_CURRENCIES,
    data: { recipientCurrencyCode, recipientCurrencies, recipientCurrencyMap },
  }
}

/**
 * Callback for {@link fetchContacts}.
 */
export function receivedContacts({
  otherContacts = [],
  topContacts = [],
  topSendContacts = [],
  topReceiveContacts = [],
}) {
  return {
    type: types.CONTACTS.RECEIVED_CONTACTS,
    data: {
      otherContacts,
      topContacts,
      topSendContacts,
      topReceiveContacts,
    },
    receivedAt: Date.now(),
  }
}

export function receivedSearchedContacts({
  topContacts = [],
  otherContacts = [],
}) {
  return {
    type: types.CONTACTS.RECEIVED_SEARCHED_CONTACTS,
    data: {
      contacts: [...topContacts, ...otherContacts],
      topContacts,
      otherContacts,
    },
    receivedAt: Date.now(),
  }
}

export function receivedContactActivityData({ transactions }, emailOrPhone) {
  return {
    type: types.CONTACTS.RECEIVED_CONTACT_DATA,
    data: {
      [emailOrPhone]: transactions,
    },
    receivedAt: Date.now(),
  }
}

/**
 * Called when first entering a flow. Fetches contacts from the server via AJAX
 * call and calls {@link receivedContacts} with the response data.
 */
export function fetchContacts() {
  return (dispatch, getState) => {
    const {
      contacts: { hasFetchedContacts, hasUpdatedContacts } = {},
      server: { env = '' },
    } = getState()
    // The pisp flow does not require fetching contacts
    // Fetching contacts can potentially cause user to be autologged out
    // if proper scopes are not in place due to being unauthorized
    if (env === ENV_TYPE.PISP) {
      return
    }

    if (!hasFetchedContacts || hasUpdatedContacts) {
      fetch('/myaccount/transfer/contacts')
        .then(response => {
          dispatch(receivedContacts(response))
        })
        .catch(handleError(dispatch))
    }
  }
}

export function searchContacts(query) {
  return (dispatch, getState) => {
    const { contacts: { contacts } = {} } = getState()
    if (contacts.length >= 100) {
      post('/myaccount/transfer/peers/search', { query })
        .then(response => {
          dispatch(
            receivedSearchedContacts({
              topContacts: [],
              otherContacts: get(response, 'directContacts', []),
            })
          )
        })
        // not essential; fail silently if a search fails
        .catch(() => {})
    }
  }
}

export function updateFavorite(contactId, isFavorite) {
  return dispatch => {
    dispatch({
      type: types.CONTACTS.FAVORITE_CONTACT,
      data: { contactId, isFavorite },
    })
    const data = { isFavorite }
    const url = `/myaccount/transfer/peers/${contactId}/favorite`
    patch(url, data)
      .then(response => {
        dispatch(updatedFavorite(contactId, isFavorite))
      })
      .catch(updatedFavorite(contactId, !isFavorite))
  }
}

export function updatedFavorite(contactId, isFavorite) {
  return {
    type: types.CONTACTS.FAVORITED_CONTACT,
    data: { contactId, isFavorite },
  }
}

export function fetchContactActivityData(emailOrPhone) {
  const url = `/myaccount/transfer/contacts/${emailOrPhone}`
  return (dispatch, getState) => {
    if (getState().contacts.activityData[emailOrPhone] || !emailOrPhone) {
      return
    }
    fetch(url)
      .then(response => {
        dispatch(receivedContactActivityData(response, emailOrPhone))
      })
      .catch(handleError(dispatch))
  }
}

export function updateContactBlock({ id = '', isBlock = false }) {
  return dispatch => {
    dispatch({ type: types.CONTACTS.UPDATE_CONTACT_BLOCK })

    return patch(`/myaccount/transfer/peers/${id}/block`, { isBlock })
      .then(() => {
        dispatch(updatedContactBlock({ id, isBlock }))

        if (isBlock) {
          dispatch(toggleContactBlockedOverpanel({ show: true }))
        } else {
          dispatch(toggleContactUnblockedOverpanel({ show: true }))
        }
      })
      .catch(handleError(dispatch))
  }
}

export function updatedContactBlock({ id = '', isBlock = false }) {
  return { type: types.CONTACTS.UPDATED_CONTACT_BLOCK, data: { id, isBlock } }
}

/**
 * Called when first entering a flow. Fetches contacts from the server via AJAX
 * call and calls {@link receivedContacts} with the response data.
 */
export function deleteContact(contactId = '') {
  return dispatch => {
    dispatch({ type: types.CONTACTS.DELETE_CONTACT, data: { contactId } })
    let url
    const data = {}
    // NOTE contact IDs from contactbookmgmtserv v1 start with CID whereas contacts from
    // contactbookmgmtserv v2 start with PID (this is an invariant that will never change)
    // which we use instead of current flow or experimentation because it is insufficient
    // TODO remove this once fully migrated to contactbookmgmtserv v2 contact model
    if (contactId && contactId[0] === 'P') {
      url = `/myaccount/transfer/peers/${contactId}`
    } else {
      url = '/myaccount/transfer/contacts'
      data.contactId = contactId
    }
    return deleteAjax(url, data)
      .then(() => {
        dispatch(deletedContact(contactId))
      })
      .catch(handleError(dispatch))
  }
}

export function deletedContact(contactId) {
  return { type: types.CONTACTS.DELETED_CONTACT, data: { contactId } }
}

/**
 * Called when move from recipient to amount page (only in default currency condition).
 * Fetches recipient details from the server via AJAX
 */
export function fetchRecipientDetails(contact, linkName, isContact, flowType) {
  // prefetch chunk file
  Preview.preload()

  contact = removeMailto(contact) // remove 'mailto:' from email
  const { email: recipient } = contact

  return (dispatch, getState) => {
    dispatch({ type: types.CROSS_BORDER.REQUEST_RECIPIENT_DETAILS }) // Show spinner
    return fetch('/myaccount/transfer/recipientDetails', {
      recipient,
      isPurchase: flowType === 'buy',
      payerId: contact.payerId,
      networkId: contact.networkId,
    })
      .then(
        ({ data: { recipientDetails = {}, beaconClientMetaDataId = '' } }) => {
          dispatch({ type: types.CROSS_BORDER.RECEIVED_RECIPIENT_DETAILS }) // Hide spinner
          if (beaconClientMetaDataId) {
            dispatch({
              type: types.TRANSACTION.SET_FRAUDNET_CLIENT_METADATA,
              data: { beaconClientMetaDataId },
            })
          }

          if (recipientDetails.triggerSenderPhoneConfirmation) {
            dispatch({ type: types.USER.TRIGGER_SENDER_PHONE_CONFIRMATION })
          }

          dispatch({
            type: types.TRANSACTION.CHANGE_HIGHLIGHTED_PAYMENT_TYPE,
            data: {
              highlightPaymentType: recipientDetails.highlightPaymentType,
            },
          })

          if (recipientDetails.paymentTypeSelectionMessage) {
            dispatch({
              type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_SELECTION_MESSAGE,
              data: {
                message: recipientDetails.paymentTypeSelectionMessage,
              },
            })
          }

          if (recipientDetails.paymentTypeTogglePersonalMessage) {
            dispatch({
              type:
                types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PERSONAL_MESSAGE,
              data: {
                message: recipientDetails.paymentTypeTogglePersonalMessage,
              },
            })
          }

          if (recipientDetails.paymentTypeTogglePurchaseMessage) {
            dispatch({
              type:
                types.TRANSACTION.UPDATE_PAYMENT_TYPE_TOGGLE_PURCHASE_MESSAGE,
              data: {
                message: recipientDetails.paymentTypeTogglePurchaseMessage,
              },
            })
          }

          dispatch({
            type: types.TRANSACTION.CHANGE_RECOMMENDED_PAYMENT_TYPE,
            data: {
              recommendedPaymentType: recipientDetails.recommendedPaymentType,
            },
          })

          if (
            recipientDetails.paymentTypeTransitions &&
            !isEmpty(recipientDetails.paymentTypeTransitions)
          ) {
            dispatch({
              type: types.TRANSACTION.UPDATE_PAYMENT_TYPE_TRANSITIONS,
              data: {
                paymentTypeTransitions: recipientDetails.paymentTypeTransitions,
              },
            })
          }

          dispatch(UnifiedActions.setContextID(recipientDetails))

          dispatch(
            selectContact(contact, linkName, isContact, recipientDetails)
          )

          // prepare data for XB
          const data = prepareXBInitialState(recipientDetails, getState())
          dispatch(setXBInitialState(data))
          // prepare data for domestic
          dispatch(updateRecipientCurrencies(recipientDetails))

          if (recipientDetails.recipientCountryIsInferred) {
            // autofill the country code for the user based on what we inferred.
            // they can change it if we got it wrong
            // this will also have the effect of always sending the country code
            // even if we're not in the RTR case (and therefore don't require
            // the user provide the country).
            dispatch(
              changeUnilateralCountryCode(recipientDetails.recipientCountryCode)
            )
          }

          routeTo(`${getUrlFragment()}/preview`)
        }
      )
      .catch(handleError(dispatch))
  }
}

export function advanceToRequestReview(pn, pg, extra) {
  // preload chunk file
  MultiRequestSplitPreview.preload()

  const linkName = 'clickRecipientNext'
  const pageGroup = pg || 'main:p2p:request::start'
  const pageName = pn || 'main:p2p:request::start:node::'

  return (dispatch, getState) => {
    const {
      transaction: { transactionIds = '' } = {},
      server: { businessAccountInfo = {}, isEnrolledInBillSplitting } = {},
      billSplitting: {
        partnerDisplayName: partnerName,
        counterParty,
        category,
      },
      multiRecipient: { recipients = [] },
    } = getState()

    // Consumer: Single Request => request
    // Consumer: Multi Request => request && flnm
    // Business: Multi Request => request::multi
    const requestBeaconType = businessAccountInfo.isBusinessUser ? 'multi' : ''
    const flnm = partnerName ? 'split_money' : 'request_multi'
    const data = {
      ...(!businessAccountInfo.isBusinessUser &&
        isEnrolledInBillSplitting && { flnm }),
      ...(partnerName && { mname: partnerName }),
      ...(counterParty && { mname: counterParty, pp_flow: '8Ball', category }),
      contact_count: recipients.length,
    }

    const nextPageGroup = `main:p2p:request:${requestBeaconType}:review`
    const nextPageName = `main:p2p:request:${requestBeaconType}:review:node::`

    dispatch({
      type: '',
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName,
        pageGroup: pageGroup,
        pageName: pageName,
        data: { ...data, transactionIds, ...extra },
        nextPageGroup: nextPageGroup,
        nextPageName: nextPageName,
        trackType: 'link',
      }),
    })
  }
}

export function selectUnilateralContact(contact) {
  return {
    type: types.RECIPIENT.SELECT_UNILATERAL_CONTACT,
    data: { contact },
  }
}

export function selectContact(
  contact,
  linkName,
  isContact,
  recipientDetails = {},
  enableAnalytics = true
) {
  const urlPathName = window.location.pathname || ''
  const isGiftRoute = urlPathName.includes('gift')

  // For unprotected goods and services
  const flowTypes = ['send', 'buy', 'gift', 'request', 'homepage']
  let flowType = flowTypes.find(flow => urlPathName.includes(flow)) || ''

  // the url is `homepage` but the actual flow type is called `unified`
  if (flowType === 'homepage') {
    flowType = 'unified'
  }

  const emailOrPhone = isEmail(contact.email) ? 'email' : 'phone'

  const pageGroup = `main:p2p:${flowType}::start`
  const pageName = `main:p2p:${flowType}::start:node::`

  const nextPageGroup = `main:p2p:${flowType}::${emailOrPhone}`
  const nextPageName = `${nextPageGroup}:node::`

  const meta = enableAnalytics
    ? getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName,
        pageGroup: pageGroup,
        pageName: pageName,
        data: {
          ...mapDefaultCurrencyKeys(recipientDetails),
          pp_flow: isGiftRoute ? 'p2p_gift' : undefined,
        },
        nextPageGroup: nextPageGroup,
        nextPageName: nextPageName,
        trackType: 'link',
      })
    : null

  if (contact && recipientDetails) {
    contact = decorateContact(contact, recipientDetails)
  }

  return {
    type: types.RECIPIENT.SELECT_CONTACT,
    meta,
    data: { contact },
  }
}

/**
 * Called when a contact is selected from ContactCircles or autocomplete
 * dropdown.
 * @param {Object} contact The contact object that was selected.
 */
export function multiSelectContact(contact) {
  return {
    type: types.RECIPIENTS.SELECT_CONTACT,
    data: { contact },
  }
}

/**
 * Called when a recipient is removed.
 * @param {Object} recipient The recipient object that was removed.
 */
export function removeRecipient(recipient) {
  return {
    type: types.RECIPIENTS.REMOVE_RECIPIENT,
    data: { recipient },
  }
}

/**
 * This action is called when a new selected recipient is new focused.
 * @param {number} index - The new index of the focused selected recipient.
 */
export function changeSelectedRecipientIndex(index) {
  return {
    type: types.RECIPIENTS.CHANGE_SELECTED_RECIPIENT_INDEX,
    data: { index },
  }
}

export function changeRecipient(recipient) {
  const recipientIsEmail = isEmail(recipient)
  const recipientIsPhone = isPhone(recipient)
  const isRecipientValid = recipientIsEmail || recipientIsPhone

  return {
    type: types.RECIPIENT.CHANGE_RECIPIENT,
    data: { recipient, isRecipientValid },
  }
}

export function changeTypedRecipient(recipient) {
  return {
    type: types.RECIPIENT.CHANGE_TYPED_RECIPIENT,
    data: { recipient },
  }
}

/**
 * Called when the value of the current typed-out recipient has changed.
 * @param {string} currentRecipient - The new value of the typed-out recipient.
 */
export function changeCurrentRecipient(currentRecipient) {
  return {
    type: types.RECIPIENTS.CHANGE_CURRENT_RECIPIENT,
    data: { currentRecipient },
  }
}

/**
 * Called when the display name of the current typed-out recipient has changed.
 * @param {string} displayName - The new value of the display name of the
 *                               typed-out recipient.
 */
export function changeCurrentRecipientDisplayName(displayName) {
  return {
    type: types.RECIPIENTS.CHANGE_CURRENT_RECIPIENT_DISPLAY_NAME,
    data: { displayName },
  }
}

export function resetMultiRecipients() {
  return (dispatch, getState) => {
    dispatch({
      type: types.RECIPIENTS.RESET_RECIPIENTS,
      data: getState().contacts,
    })
  }
}

export function changeAmount(amount) {
  return {
    type: types.AMOUNT.CHANGE_AMOUNT,
    data: { amount },
  }
}

export function changeCVVCode(cvv) {
  return {
    type: types.TRANSACTION.CHANGE_CVV_CODE,
    data: { cvv },
  }
}

export function toggleSenderAccountInfo() {
  return {
    type: types.USER.TOGGLE_SENDER_HELPER_INFORMATION,
  }
}

/**
 * Called when currency code is updated in Preview page
 * @param {string} newCurrency
 */
export function changeCurrency(newCurrency) {
  return (dispatch, getState) => {
    const { currencyCode } = getState().amount
    if (newCurrency !== currencyCode) {
      dispatch({
        type: types.AMOUNT.CHANGE_CURRENCY,
        data: {
          currencyCode: newCurrency,
        },
      })
    }
  }
}
/**
 * Called when currency code is updated in Request Preview page
 * @param {string} newCurrency
 */
export function changeRequestCurrency(newCurrency) {
  return (dispatch, getState) => {
    const { currencyCode } = getState().amount
    if (newCurrency !== currencyCode) {
      dispatch({
        type: types.AMOUNT.CHANGE_REQUEST_CURRENCY,
        data: {
          requestCurrencyCode: newCurrency,
        },
      })
    }
  }
}

export function changeNote(note) {
  return {
    type: types.NOTE.CHANGE_NOTE,
    data: { note },
  }
}

export function mandatoryNoteMissing(missing) {
  return {
    type: types.NOTE.REQUIRED_FIELD_ERROR,
    data: { missing },
  }
}

export function changeGiftOption(giftOption) {
  return {
    type: types.GIFT_OPTIONS.CHANGE_OPTION,
    data: { giftOption },
  }
}

export function routeToPPDG(recipient) {
  return dispatch => {
    return post('/myaccount/transfer/ppdg', { recipient })
      .then(response => {
        if (response && response.data) {
          window.location = `${window.location.protocol}//${window.location.host}/gifts/categories/send-a-gift?ctxId=${response.data}`
        } else {
          handleError(dispatch)
        }
      })
      .catch(handleError(dispatch))
  }
}

export function changeGiftAidCheckbox(flag) {
  return {
    type: types.EXTERNAL.CHANGE_GIFTAID,
    data: flag,
  }
}

/**
 * Callback for {@link requestMoney}.
 * @param {Object} data - Response data from server.
 */
export function requestedMoney(data) {
  return {
    type: types.TRANSACTION.REQUESTED_MONEY,
    data: data,
    requestedAt: Date.now(),
  }
}

function isAgreementConsentRequired(
  userAgreementConsentRequired,
  hasResolvedUserAgreementConsent
) {
  return userAgreementConsentRequired && !hasResolvedUserAgreementConsent
}

/**
 * Posts request-money request to the server and calls {@link requestedMoney}
 * with the response data.
 */
export function requestMoney({
  recipient,
  recipients,
  amount,
  currencyCode,
  note,
  purchaseConsentChecked,
  purchaseConsentRequired,
  userAgreementConsentRequired,
  userAgreementConsentMinVersion,
  userAgreementConsentMaxVersion,
  hasResolvedUserAgreementConsent,
}) {
  // preload chunk file
  RequestSuccess.preload()

  // Allows for compatibility between single-recipient and multi-recipient flows.
  const recipientField = recipient
    ? { recipient }
    : {
        recipients: recipients.map(_recipient => ({
          recipient: _recipient.recipient,
          payerId: _recipient.payerId,
        })),
      }

  return (dispatch, getState) => {
    const {
      server: {
        env,
        psd2: { token: psd2Token = '' },
        businessAccountInfo = {},
        isEnrolledInBillSplitting,
        hasConfirmedEmail,
      } = {},
    } = getState()

    const requestBeaconType = businessAccountInfo.isBusinessUser ? 'multi' : ''
    const ct_cnt = recipients && recipients.length

    // Consumer: Single Request => request
    // Consumer: Multi Request => request && flnm
    // Business: Multi Request => request::multi
    const analyticsData = {
      ct_cnt,
      ...(!businessAccountInfo.isBusinessUser &&
        isEnrolledInBillSplitting && { flnm: 'request_multi' }),
      email: hasConfirmedEmail ? 'confirmed' : 'not_confirmed',
    }

    const pageGroup = `main:p2p:request:${requestBeaconType}:review`
    const pageName = `main:p2p:request:${requestBeaconType}:review:node::`
    const nextPageGroup = `main:p2p:request:${requestBeaconType}:done`
    const nextPageName = `main:p2p:request:${requestBeaconType}:done:node::`

    dispatch({
      type: types.TRANSACTION.REQUEST_MONEY,
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName: 'requestDone',
        pageGroup,
        pageName,
        data: analyticsData,
        persistKey: 'regularRequest',
        nextPageGroup,
        nextPageName,
        trackType: 'link',
      }),
    })

    const data = {
      env,
      psd2Token,
      ...recipientField,
      amount,
      currencyCode,
      description: note.trim(), // trim the note before submitting
      purchaseConsentChecked,
      purchaseConsentRequired,
    }

    if (
      isAgreementConsentRequired(
        userAgreementConsentRequired,
        hasResolvedUserAgreementConsent
      )
    ) {
      tracking(
        Events.active_consent_disclaimer_agree_pressed({
          flow: 'p2p_request_money',
          policy_major_version: userAgreementConsentMaxVersion,
          policy_minor_version: userAgreementConsentMinVersion,
        })
      )

      data.userAgreementConsentRequired = userAgreementConsentRequired
      data.userAgreementConsentMinVersion = userAgreementConsentMinVersion
      data.userAgreementConsentMaxVersion = userAgreementConsentMaxVersion
    }

    post('/myaccount/transfer/request', data)
      .then(response => {
        const {
          data: responseData = {},
          ppmeClaimUrl = '',
          showPPMeIframe = false,
        } = response
        const {
          amountFormatted,
          requestId,
          requestLegId,
          payRequestLink,
          transactionIds,
        } = responseData
        const requestedMoneyPayload = {
          amountFormatted,
          requestId,
          requestLegId,
          ppmeClaimUrl,
          showPPMeIframe,
          transactionIds,
          payRequestLink,
        }

        addCPLData({ req_id: requestId, trid: transactionIds })
        setPersistentData('regularRequest', {
          req_id: requestId,
          trid: transactionIds,
        })

        return fetch('/myaccount/transfer/personalization/impression/')
          .then(result => {
            const pStudioContent = get(
              result,
              'data.clientData.pStudioContent',
              {}
            )
            requestedMoneyPayload.pStudioContent = pStudioContent
          })
          .catch(handleError(dispatch))
          .finally(() => {
            dispatch(requestedMoney(requestedMoneyPayload))

            if (
              isAgreementConsentRequired(
                userAgreementConsentRequired,
                hasResolvedUserAgreementConsent
              )
            ) {
              dispatch({
                type: types.TRANSACTION.RESOLVE_USER_AGREEMENT_CONSENT,
              })
            }
            routeTo(`${getUrlFragment().replace('preview', 'success')}`, {
              replace: true,
            })
          })
      })
      .catch(handleError(dispatch))
  }
}

/**
 * Called when the user leaves the success page.
 */
export function leavingSuccessPage() {
  return {
    type: types.TRANSACTION.LEAVING_SUCCESS_PAGE,
  }
}

/**
 * Called when the user presses the "Cancel" button.
 */
export function cancelledTransaction() {
  return {
    type: types.TRANSACTION.CANCELLED_TRANSACTION,
  }
}

export function submittingRecipient({ selectedType = '' } = {}) {
  return {
    type: types.TRANSACTION.SUBMITTING_RECIPIENT,
    data: {
      selectedType,
    },
  }
}

/**
 * Cross Border Stuff
 */

export function changeDestinationCurrencyCode(code, { shouldFireAnalytics }) {
  const pagename = 'main:consumer:p2p:transfer:contact'
  return {
    type: types.CROSS_BORDER.CHANGE_DESTINATION_CURRENCY_CODE,
    meta:
      shouldFireAnalytics &&
      getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName: 'currencyChange',
        pageGroup: pagename,
        pageName: pagename,
        data: { rcur: code },
        trackType: 'link',
      }),
    data: {
      currencyCode: code,
    },
  }
}

export function changeOriginCurrencyCode({
  fromCurrencyCode,
  shouldFireAnalytics = true,
}) {
  const pagename = 'main:consumer:p2p:transfer:contact'
  return {
    type: types.CROSS_BORDER.CHANGE_ORIGIN_CURRENCY_CODE,
    meta:
      shouldFireAnalytics &&
      getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION_WITH_DATA, {
        linkName: 'currencyChange',
        pageGroup: pagename,
        pageName: pagename,
        data: { sndcc: fromCurrencyCode },
        trackType: 'link',
      }),
    data: {
      currencyCode: fromCurrencyCode,
    },
  }
}

export function fetchOriginAmount(data) {
  return getFetch(data, {
    url: '/myaccount/transfer/fx/rates/received',
    currencyCode: data.toCurrencyCode,
    preFetchAction: requestOriginAmount,
    onFetchSuccessAction: receiveOriginAmount,
  })
}

export function fetchDestinationAmount(data) {
  return getFetch(data, {
    url: '/myaccount/transfer/fx/rates/sent',
    currencyCode: data.fromCurrencyCode,
    preFetchAction: requestDestinationAmount,
    onFetchSuccessAction: receiveDestinationAmount,
  })
}

export function fetchExchangeRate(data) {
  return function doFetch(dispatch, getState) {
    const { fromCurrencyCode, toCurrencyCode, isPurchase } = data
    const { token } = getState().recipient
    // we're overriding the amount because we don't care about the amount,
    // it just needs to be something so we can make the call to get the exchange rate
    const fakeAmount = '1'
    const fakeToCountryCode = 'US'
    const fakeFromCountryCode = 'US'
    // once we get a proper endpoint, we wont need to fake out the amount
    // but for now, we'll just use this endpoint
    const url = '/myaccount/transfer/fx/rates/sent'
    const currentTime = Date.now()
    dispatch(
      requestExchangeRate({
        ...data,
        latestExchangeRateFetchTime: currentTime,
      })
    )

    const dataToSend = {
      amount: fakeAmount,
      toCountryCode: fakeToCountryCode,
      fromCountryCode: fakeFromCountryCode,
      // the next two fields are the only things that really matter for this call
      // but all fields are required for the call to succeed
      fromCurrencyCode,
      toCurrencyCode,
      requestedTime: currentTime,
      isPurchase,
      token,
    }
    return fetch(url, dataToSend)
      .then(response => {
        const { latestExchangeRateFetchTime } = getState().crossBorder // Yeah it is `crossBorder` rather than `loading` here
        if (
          !requestMatchesResponse(
            latestExchangeRateFetchTime,
            response.data.requestedTime
          )
        ) {
          return
        }

        dispatch(receiveExchangeRate(data, response))
        dispatch(updateFxQuoteId(response.data.fxQuoteId))
        return response
      })
      .catch(handleErrorFromFailedRequest(dispatch))
  }
}

/**
 * Risk merchandise
 */
export function changePurchaseLocation(mtgplace) {
  return {
    type: types.TRANSACTION.CHANGE_PURCHASE_LOCATION,
    data: { mtgplace },
  }
}

export function changeItemCategory(category) {
  return {
    type: types.TRANSACTION.CHANGE_ITEM_CATEGORY,
    data: { category },
  }
}

export function updateFxQuoteId(fxQuoteId) {
  return {
    type: types.CROSS_BORDER.RECEIVED_FX_QUOTE_ID,
    data: {
      fxQuoteId: fxQuoteId || '',
    },
  }
}

/**
 * This function abstracts common logic between the fetchOriginAmount and fetchDestinationAmount
 * actions. It returns a function that accepts a `dispatch` which it will use to dispatch events
 * before and after the fetch request (on succes or failure). It allows you to configure what
 * action creators to call to generate the action for the dispatch for each of these.
 * @param {Object} data - the data needed for the request and determining whether the request should be sent
 * @param {Object} options - The options for what URL to hit, the currencyCode to use for precision and action creators to call
 * @return Function - the function to invoke that accepts a dispatch and performs the request
 */
function getFetch(data, options) {
  const {
    amount,
    previousAmount,
    fromCurrencyCode,
    fromCountryCode,
    toCurrencyCode,
    previousCurrencyCode,
    toCountryCode,
    currencyChanged,
    isPurchase,
    token,
  } = data
  const { url, currencyCode, preFetchAction, onFetchSuccessAction } = options

  const preciseAmount = formatLocalizedNumber(currencyCode, amount)
  if (
    !shouldFetchNewAmount(
      formatUSNumber(preciseAmount),
      formatUSNumber(previousAmount),
      currencyCode,
      previousCurrencyCode
    )
  ) {
    if (currencyChanged) {
      return fetchExchangeRate(data)
    }
    return function noop() {}
  }

  return function doFetch(dispatch, getState) {
    // Reference:
    // http://stackoverflow.com/questions/35667249/accessing-redux-state-in-an-action-creator
    const currentTime = Date.now()
    const dataWithPreciseAmount = {
      ...data,
      // we need to override the amount
      amount: preciseAmount,
      latestAmountFetchTime: currentTime,
    }

    dispatch(preFetchAction(dataWithPreciseAmount))

    const dataToSend = {
      amount: preciseAmount, // TODO: remove trailing `.` on server
      fromCurrencyCode,
      toCurrencyCode,
      fromCountryCode,
      toCountryCode,
      requestedTime: currentTime,
      isPurchase,
      token,
    }

    return fetch(url, dataToSend)
      .then(response => {
        const { latestAmountFetchTime } = getState().crossBorder // Yeah it is `crossBorder` rather than `loading` here
        if (
          !requestMatchesResponse(
            latestAmountFetchTime,
            response.data.requestedTime
          )
        ) {
          return
        }

        // Only dispatch the response if the latestAmountFetchTime and requestedTime match
        dispatch(onFetchSuccessAction(dataWithPreciseAmount, response))
        dispatch(receiveFees(dataWithPreciseAmount, response))
        dispatch(
          changeAmount(
            formatLocalizedNumber(toCurrencyCode, response.data.toAmountRaw)
          )
        )
        dispatch(changeCurrency(toCurrencyCode))
        dispatch(updateFxQuoteId(response.data.fxQuoteId))
        return response
      })
      .catch(err => {
        dispatch(fetchConversionFailed())
        handleErrorFromFailedRequest(dispatch)(err)
      })
  }
}

export function changeDestinationAmount(data) {
  const amountPrecise = formatLocalizedNumber(data.toCurrencyCode, data.amount)
  return {
    type: types.CROSS_BORDER.CHANGE_DESTINATION_AMOUNT,
    // meta: data.isInitialAmountEntry && analyticsMeta('xbDestinationAmountTyped', data),
    data: {
      amountPrecise,
      amountIsZero: isZero(amountPrecise),
      ...data,
      isInDebounceWindow: !isZero(amountPrecise),
    },
  }
}

export function handleErrorFromFailedRequest(dispatch) {
  return function onError(err) {
    dispatch(showError(err))
    return Promise.reject(err)
  }
}

function receiveFees(data, json) {
  return {
    type: types.CROSS_BORDER.RECEIVED_FEE,
    data: {
      ...json.data.fees, // { bank: { fee, totalCost }, card... }
      fxQuoteId: json.data.fxQuoteId,
      receivedAt: Date.now(),
    },
  }
}

function requestExchangeRate(data) {
  return {
    type: types.CROSS_BORDER.REQUEST_EXCHANGE_RATE,
    data,
  }
}

function receiveExchangeRate(data, json) {
  return {
    type: types.CROSS_BORDER.RECEIVED_EXCHANGE_RATE,
    data: json.data,
    receivedAt: Date.now(),
  }
}

function fetchConversionFailed() {
  return {
    type: types.CROSS_BORDER.FETCH_CONVERSION_FAILED,
  }
}

export function changeOriginAmount(data) {
  const amountPrecise = formatLocalizedNumber(
    data.fromCurrencyCode,
    data.amount
  )

  return {
    type: types.CROSS_BORDER.CHANGE_ORIGIN_AMOUNT,
    // meta: data.isInitialAmountEntry && getAalyticsMeta('xbOriginAmountTyped', data),
    data: {
      amountPrecise,
      amountIsZero: isZero(amountPrecise),
      ...data,
      isInDebounceWindow: !isZero(amountPrecise),
    },
  }
}

export function requestOriginAmount(data) {
  return {
    type: types.CROSS_BORDER.REQUEST_ORIGIN_AMOUNT,
    data,
  }
}

export function receiveOriginAmount(data, json) {
  // replace the amount in the URL with the new from amount
  const preciseAmounts = getAmountsForPrecision(data, json.data)
  return {
    type: types.CROSS_BORDER.RECEIVED_ORIGIN_AMOUNT,
    meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION, {
      linkName: 'originAmountChanged',
    }),
    data: {
      ...json.data,
      ...preciseAmounts,
    },
    receivedAt: Date.now(),
  }
}

/**
 * This is here as a helper to several actions that receive a raw amounts
 * and we need to account for precision
 */
function getAmountsForPrecision(fromAmount, toAmount) {
  const { fromCurrencyCode, toCurrencyCode } = fromAmount
  const { fromAmountRaw, toAmountRaw } = toAmount
  return {
    fromAmountPrecise: formatLocalizedNumber(fromCurrencyCode, fromAmountRaw),
    toAmountPrecise: formatLocalizedNumber(toCurrencyCode, toAmountRaw),
  }
}

// requestDestinationAmount destination amount and fee info...
// should we bundle all of them here? Wonder if that would make sense?
export function requestDestinationAmount(data) {
  return {
    type: types.CROSS_BORDER.REQUEST_DESTINATION_AMOUNT,
    data,
  }
}

export function receiveDestinationAmount(data, json) {
  return {
    type: types.CROSS_BORDER.RECEIVED_DESTINATION_AMOUNT,
    meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.ACTION, {
      linkName: 'destinationAmountChanged',
    }),
    data: {
      ...json.data,
      ...getAmountsForPrecision(data, json.data),
    },
    receivedAt: Date.now(),
  }
}

export function setXBInitialState(data) {
  return {
    type: types.CROSS_BORDER.SET_INITIAL_STATE,
    data,
  }
}

/**
 * @param {boolean} unavailableFI - Flag to set warning for Add Bank flow.
 */
export function noFundingSource(unavailableFI) {
  return {
    type: types.TRANSACTION.NO_FUNDING_SOURCE,
    data: { unavailableFI },
  }
}

// trigger hook that will be picked up the the
// external hooks middleware
export function triggerExternalHook(hook, data) {
  return {
    type: `externalHook:${hook}`,
    data,
  }
}

// Initialize FX Calculator for users focusing on single amount field (when user comes from XB flow)
export function initXBState() {
  return function(dispatch, getState) {
    const {
      amount: { fromCurrencyCode, currencyMap, currencyCode },
      user: { country: countryCode },
    } = getState()
    const currencyList = Object.keys(currencyMap).map(currency => {
      return { code: currency, symbol: currencyMap[currency] }
    })
    const currencyCodeList = currencyList.map(x => x.code)

    // Initialize XB store
    dispatch(
      setXBInitialState({
        fromCurrencyCode,
        fromCurrencyCodeList: currencyCodeList,
        showXBCalculator: true,
        toCurrencySymbolMap: currencyMap,
        toCurrencyCodeList: currencyCodeList,
        fromCountryCode: countryCode,
        toCountryCode: currencyCode,

        // don't allow auto focus on the sender amount input because it will be retrieved from server
      })
    )
    // Set the current currency as recipient currency
    dispatch(changeCurrency(currencyCode))
  }
}

// Initialize FX Calculator for users selecting a different currency in single amount field
export function initFXCalculator(toAmount, toCurrencyCode) {
  return function(dispatch, getState) {
    const {
      amount: { fromCurrencyCode, currencyMap },
      user: { country: countryCode },
      crossBorder: { fromCurrencyCodeList = [], toCurrencyCodeList = [] },
      recipient: { token },
    } = getState()
    const defaultCurrencyCodeList = Object.keys(currencyMap)

    // Initialize XB store
    dispatch(
      setXBInitialState({
        fromCurrencyCode,
        fromCurrencyCodeList: fromCurrencyCodeList.length
          ? fromCurrencyCodeList
          : defaultCurrencyCodeList,
        showXBCalculator: true,
        toCurrencyCode: toCurrencyCode,
        toCurrencyCodeList: toCurrencyCodeList.length
          ? toCurrencyCodeList
          : defaultCurrencyCodeList,
        toCurrencySymbolMap: currencyMap,
        fromCountryCode: countryCode,
        toCountryCode: countryCode,
      })
    )

    // Set the current currency as recipient currency
    dispatch(
      changeDestinationCurrencyCode(toCurrencyCode, {
        shouldFireAnalytics: true,
      })
    )

    // Set the current amount as recipient amount
    dispatch(
      changeDestinationAmount({
        toCurrencyCode: toCurrencyCode,
        amount: toAmount,
        isInitialAmountEntry: false,
        previousAmount: toAmount,
      })
    )

    // Fetch origin amount as well as exchange rate
    dispatch(
      fetchOriginAmount({
        fromCurrencyCode,
        amount: toAmount,
        toCurrencyCode: toCurrencyCode,
        currencyChanged: true,
        fromCountryCode: countryCode,
        toCountryCode: countryCode,
        token: token,
      })
    )
  }
}

export function toggleShareAddress(shouldShareAddress) {
  return {
    type: types.EXTERNAL.TOGGLE_SHARE_ADDRESS,
    data: { shouldShareAddress },
  }
}

export function setIsLoading(isLoading) {
  return {
    type: types.USER.SET_IS_LOADING,
    data: { isLoading },
  }
}

/**
 * Capture /external query params including: currencyCode,
 * toCountry, recipient, amount, etc.
 */
export function captureExternalParams(data) {
  return { type: types.EXTERNAL.RECEIVED_EXTERNAL_PARAMS, data }
}

export function captureExternalIntegrationParams(data) {
  return { type: types.EXTERNAL.RECEIVED_EXTERNAL_INTEGRATION_PARAMS, data }
}
export function redirect() {
  return { type: types.ROUTER.REDIRECT }
}

export function setSuggestedTransactionType(transactionType) {
  return {
    type: types.TRANSACTION.SET_SUGGESTED_TRANSACTION_TYPE,
    data: { transactionType },
  }
}

export function setServiceReturnedPaymentType(transactionType) {
  return {
    type: types.TRANSACTION.SET_SERVICE_RETURNED_PAYMENT_TYPE,
    data: { transactionType },
  }
}

export function setPaymentTypeSelected(paymentTypeIsSelected) {
  return {
    type: types.TRANSACTION.SELECT_PAYMENT_TYPE,
    data: { paymentTypeIsSelected },
  }
}
